"use client";

/**
 * AiTextPopover — floating "rewrite with AI" popover for the Marp markdown
 * editor (Wave 2, May 2026).
 *
 * Mirrors `AiCellPopover.tsx` conventions (orange Sparkles eyebrow, ink-3
 * monospace selection preview, primary/ghost CTA pair, role="dialog" +
 * aria-label) but is anchored manually via absolute positioning rather
 * than Univer's canvas popup layer — the host (MarpDeckEditor) computes
 * the anchor from the Tiptap selection rect.
 *
 * Flow:
 *   1. User selects text in the markdown editor → host mounts this popover.
 *   2. User types an instruction (free-text) and hits "Genera".
 *   3. We POST to `/api/v1/marp/{contentId}/ai-rewrite` and surface the
 *      rewritten preview with Accetta / Annulla CTAs.
 *   4. On Accetta → host applies the rewrite to the markdown via string
 *      slice (we do not own the buffer; we only emit the new text).
 *
 * Accessibility:
 *   - `role="dialog"` + `aria-label` so SR users land here when the popover
 *     mounts.
 *   - ESC closes via a document-level keydown listener (not a backdrop —
 *     a backdrop would steal focus from the Tiptap selection).
 *   - Click-outside closes via a `pointerdown` listener; clicks inside the
 *     popover root are ignored.
 *   - The textarea autoFocuses so the keyboard path is one-shot.
 */

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Check, X } from "lucide-react";
import { AndoLogo } from "../ando/AndoLogo";
import * as React from "react";

import { cn } from "../lib/cn";
import { Button } from "../primitives/button";

export interface AiTextPopoverProps {
  /**
   * content_items.id — used by the DEFAULT (marp) rewrite transport. Ignored
   * when ``onSubmit`` is provided. Optional so docx/xlsx hosts (which inject
   * ``onSubmit``) don't need a marp content id.
   */
  readonly contentId?: string;
  /**
   * Endpoint-agnostic rewrite transport. When provided, the popover calls this
   * instead of the built-in marp ai-rewrite fetch — so docx/xlsx hosts can
   * route the rewrite through ``/documents/{id}/region-edit`` (→ the unified
   * doc_diff_payload redline rail) while marp keeps its existing path. DRY: one
   * popover, per-host transport.
   */
  readonly onSubmit?: (
    instruction: string,
  ) => Promise<{ rewritten: string; rationale?: string | null }>;
  /** The text the user highlighted in the editor (shown for context). */
  readonly selectedText: string;
  /**
   * Optional 2 surrounding paragraphs to give the LLM more context. The
   * caller (MarpDeckEditor) computes these from the markdown source.
   */
  readonly contextParagraphs?: string;
  /**
   * Apply callback — fired when the user clicks Accetta on the preview.
   * The host replaces the original selection range with `rewritten`.
   */
  readonly onApplyRewrite: (rewritten: string) => void;
  /** Close the popover (ESC, click outside, X button, or post-accept). */
  readonly onClose: () => void;
  /**
   * Absolute anchor (top-left of the popover). The host computes this from
   * the Tiptap selection's `getBoundingClientRect()`. Optional so the
   * component degrades to a centered floating card when no anchor is
   * provided (used in smoke tests / Storybook).
   */
  readonly anchor?: { top: number; left: number };
  readonly className?: string;
}

interface AiRewriteResponse {
  rewritten: string;
  rationale?: string | null;
}

const PREVIEW_TRUNCATE = 120;

function truncate(value: string, max: number): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max)}…`;
}

export const AiTextPopover: React.FC<AiTextPopoverProps> = ({
  contentId,
  onSubmit,
  selectedText,
  contextParagraphs,
  onApplyRewrite,
  onClose,
  anchor,
  className,
}) => {
  const { i18n } = useLingui();
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const [instruction, setInstruction] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [preview, setPreview] = React.useState<string | null>(null);
  const [rationale, setRationale] = React.useState<string | null>(null);

  // Stable refs for the document listeners so we don't re-bind on every
  // keystroke in the instruction box.
  const onCloseRef = React.useRef(onClose);
  React.useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // ESC closes (matches AiCellPopover semantics).
  React.useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onCloseRef.current();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Click outside closes. We use pointerdown (not click) so the popover
  // dismisses BEFORE the editor below would re-claim the selection.
  React.useEffect(() => {
    const handler = (event: PointerEvent) => {
      const root = rootRef.current;
      if (!root) return;
      if (event.target instanceof Node && root.contains(event.target)) return;
      onCloseRef.current();
    };
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, []);

  // Autofocus the textarea on mount so the keyboard path is one-shot.
  React.useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleSubmit = React.useCallback(async () => {
    if (loading) return;
    const trimmed = instruction.trim();
    if (!trimmed) {
      setError(i18n._(msg`Inserisci un'istruzione (es. 'rendi più formale').`));
      return;
    }
    setError(null);
    setLoading(true);
    try {
      let data: AiRewriteResponse;
      if (onSubmit) {
        // Host-injected transport (docx/xlsx → /documents/{id}/region-edit).
        data = await onSubmit(trimmed);
      } else {
        // Default marp ai-rewrite transport (back-compat).
        const response = await fetch(
          `/api/v1/marp/${encodeURIComponent(contentId ?? "")}/ai-rewrite`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              selected_text: selectedText,
              context_paragraphs: contextParagraphs ?? "",
              instruction: trimmed,
            }),
          },
        );
        if (!response.ok) {
          const detail = await response.text().catch(() => "");
          throw new Error(
            detail ||
              i18n._(msg`Riscrittura fallita (HTTP ${response.status}).`),
          );
        }
        data = (await response.json()) as AiRewriteResponse;
      }
      if (typeof data.rewritten !== "string" || !data.rewritten) {
        throw new Error(i18n._(msg`Risposta AI vuota.`));
      }
      setPreview(data.rewritten);
      setRationale(
        typeof data.rationale === "string" && data.rationale.length > 0
          ? data.rationale
          : null,
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : i18n._(msg`Errore inaspettato.`),
      );
    } finally {
      setLoading(false);
    }
  }, [contentId, contextParagraphs, i18n, instruction, loading, onSubmit, selectedText]);

  const handleAccept = React.useCallback(() => {
    if (!preview) return;
    onApplyRewrite(preview);
    onClose();
  }, [onApplyRewrite, onClose, preview]);

  const handleReset = React.useCallback(() => {
    setPreview(null);
    setRationale(null);
    setError(null);
  }, []);

  const positionStyle: React.CSSProperties | undefined = anchor
    ? { position: "absolute", top: anchor.top, left: anchor.left, zIndex: 60 }
    : undefined;

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-label={i18n._(msg`Riscrivi la selezione con AI`)}
      style={positionStyle}
      className={cn(
        "w-[320px] overflow-hidden rounded-lg border border-hairline bg-card",
        "shadow-[0_8px_24px_-8px_rgba(0,0,0,0.18)]",
        "ring-1 ring-ando-orange-soft/40",
        className,
      )}
      onPointerDown={(event) => {
        // Prevent the document-level outside handler from firing for clicks
        // that originated inside the popover.
        event.stopPropagation();
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-1.5 border-hairline border-b bg-ando-orange-soft/40 px-3 py-1.5">
        <AndoLogo size={12} className="shrink-0" aria-hidden />
        <span className="font-medium text-[10.5px] text-ando-orange uppercase tracking-[0.06em]">
          {i18n._(msg`Riscrivi con AI`)}
        </span>
        <button
          type="button"
          aria-label={i18n._(msg`Chiudi`)}
          onClick={onClose}
          className="ml-auto inline-flex h-5 w-5 items-center justify-center rounded-sm text-ink-3 transition-colors hover:bg-ink-1/[0.04] hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45"
        >
          <X className="h-3 w-3" aria-hidden strokeWidth={2} />
        </button>
      </div>

      {/* Body */}
      <div className="space-y-2.5 px-3 py-2.5">
        {/* Selected text preview */}
        <div>
          <div className="mb-1 font-medium text-[10px] text-ink-3 uppercase tracking-[0.06em]">
            {i18n._(msg`Selezione`)}
          </div>
          <p
            className="overflow-hidden font-mono text-[11px] text-ink-3 leading-snug"
            title={selectedText}
          >
            {truncate(selectedText, PREVIEW_TRUNCATE)}
          </p>
        </div>

        {preview === null ? (
          <>
            {/* Instruction box */}
            <div>
              <label
                htmlFor={`ai-text-instruction-${contentId}`}
                className="mb-1 block font-medium text-[10px] text-ink-3 uppercase tracking-[0.06em]"
              >
                {i18n._(msg`Istruzione`)}
              </label>
              <textarea
                ref={textareaRef}
                id={`ai-text-instruction-${contentId}`}
                value={instruction}
                onChange={(event) => setInstruction(event.target.value)}
                onKeyDown={(event) => {
                  if (
                    (event.metaKey || event.ctrlKey) &&
                    event.key === "Enter"
                  ) {
                    event.preventDefault();
                    void handleSubmit();
                  }
                }}
                placeholder={i18n._(
                  msg`Es. 'rendi più formale', 'accorcia a 2 righe', 'in inglese'`,
                )}
                rows={3}
                className={cn(
                  "block w-full resize-none rounded-md border border-hairline bg-white/85 px-2 py-1.5 text-[12px] text-ink-1",
                  "placeholder:text-ink-3 focus:border-ando-orange/60 focus:outline-none focus:ring-2 focus:ring-ando-orange/30",
                )}
              />
            </div>
            {error ? (
              <p
                role="alert"
                className="text-[11px] text-rose-600 leading-snug"
              >
                {error}
              </p>
            ) : null}
          </>
        ) : (
          <>
            {/* Rewritten preview */}
            <div>
              <div className="mb-1 font-medium text-[10px] text-ando-orange uppercase tracking-[0.06em]">
                {i18n._(msg`Proposta AI`)}
              </div>
              <p className="max-h-[140px] overflow-y-auto whitespace-pre-wrap text-[12px] text-ink-1 leading-snug">
                {preview}
              </p>
            </div>
            {rationale ? (
              <p className="max-h-[80px] overflow-y-auto whitespace-pre-wrap text-[11px] text-ink-3 italic leading-snug">
                {rationale}
              </p>
            ) : null}
          </>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 border-hairline border-t bg-cream/60 px-2 py-1.5">
        {preview === null ? (
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="flex-1"
            >
              {i18n._(msg`Annulla`)}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => void handleSubmit()}
              disabled={loading || instruction.trim().length === 0}
              className="flex-1"
              aria-label={i18n._(msg`Genera riscrittura`)}
            >
              {loading ? (
                <Spinner />
              ) : (
                <AndoLogo size={12} aria-hidden />
              )}
              {i18n._(msg`Genera`)}
            </Button>
          </>
        ) : (
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="flex-1"
            >
              <X className="h-3 w-3" aria-hidden strokeWidth={2} />
              {i18n._(msg`Riprova`)}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleAccept}
              className="flex-1"
              aria-label={i18n._(msg`Applica riscrittura`)}
            >
              <Check className="h-3 w-3" aria-hidden strokeWidth={2} />
              {i18n._(msg`Accetta`)}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
AiTextPopover.displayName = "AiTextPopover";

const Spinner: React.FC = () => {
  const { i18n } = useLingui();
  return (
  <svg
    className="h-3 w-3 animate-spin text-white/85"
    viewBox="0 0 24 24"
    role="img"
    aria-label={i18n._(msg`In corso`)}
  >
    <title>{i18n._(msg`In corso`)}</title>
    <circle
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeOpacity="0.3"
      strokeWidth="3"
      fill="none"
    />
    <path
      d="M22 12a10 10 0 0 1-10 10"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
  );
};
